import { useEffect, useState } from "react";
import { ShoppingCart, IndianRupee, Boxes, AlertTriangle, Store } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { StatCard, PageHeader } from "@/components/portal/widgets";
import api, { inr } from "@/lib/api";

export default function FranchiseeDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/franchise/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <p className="text-muted-foreground">Loading store dashboard…</p>;

  return (
    <div className="animate-fade-up">
      <PageHeader title={d.store.name} subtitle={`${d.store.address} · ${d.store.hours}`} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard testid="fstat-revenue" icon={IndianRupee} label="Revenue" value={inr(d.revenue)} accent="emerald" />
        <StatCard testid="fstat-orders" icon={ShoppingCart} label="Orders" value={d.total_orders} accent="orange" />
        <StatCard testid="fstat-inventory" icon={Boxes} label="SKUs In Stock" value={d.inventory_count} accent="sky" />
        <StatCard testid="fstat-lowstock" icon={AlertTriangle} label="Low Stock" value={d.low_stock_count} accent="violet" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <Card className="lg:col-span-2 p-6 border-slate-200/80 dark:border-slate-800">
          <h3 className="font-heading font-semibold mb-4">Weekly Sales</h3>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={d.sales_series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="day" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip formatter={(v) => inr(v)} />
              <Line type="monotone" dataKey="sales" stroke="#eab308" strokeWidth={3} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-6 border-slate-200/80 dark:border-slate-800">
          <h3 className="font-heading font-semibold mb-4 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-orange-500" /> Low Stock Alerts</h3>
          <div className="space-y-2">
            {d.low_stock_items.length === 0 && <p className="text-sm text-muted-foreground">All stocked up!</p>}
            {d.low_stock_items.map((i) => (
              <div key={i.id} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-100 dark:border-slate-800/50">
                <span className="truncate">{i.name}</span>
                <Badge className="bg-red-100 text-red-700">{i.stock} left</Badge>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card className="p-6 border-slate-200/80 dark:border-slate-800">
        <h3 className="font-heading font-semibold mb-4">Recent Orders</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-muted-foreground border-b border-slate-200 dark:border-slate-800">
              <th className="py-2 pr-4">Reference</th><th className="py-2 pr-4">Customer</th><th className="py-2 pr-4">Total</th><th className="py-2">Status</th>
            </tr></thead>
            <tbody>
              {d.recent_orders.map((o) => (
                <tr key={o.id} className="border-b border-slate-100 dark:border-slate-800/50">
                  <td className="py-2.5 pr-4 font-mono text-xs">{o.reference}</td>
                  <td className="py-2.5 pr-4">{o.customer_name}</td>
                  <td className="py-2.5 pr-4 font-semibold">{inr(o.total)}</td>
                  <td className="py-2.5"><Badge variant="secondary" className="capitalize">{o.status}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
